import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Gift, Loader2, ChevronRight, Calendar, DollarSign } from "lucide-react";
import { API } from "@/App";
import axios from "axios";

const CATEGORIES = [
  "Large Cap", "Mid Cap", "Small Cap", "Multi Cap", "ELSS",
  "Hybrid - Aggressive", "Debt - Corporate Bond"
];

export const HighestDividendPage = () => {
  const [category, setCategory] = useState("Large Cap");
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchFunds = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/funds/search?q=${category} IDCW&limit=20`);
        const idcwFunds = (res.data.funds || [])
          .filter(f => f.option === "IDCW" || f.schemeName.toLowerCase().includes("idcw") || f.schemeName.toLowerCase().includes("dividend"))
          .map(fund => ({
            ...fund,
            // Mock dividend data
            lastDividend: (Math.random() * 5 + 0.5).toFixed(2),
            dividendYield: (Math.random() * 8 + 1).toFixed(2),
            frequency: ["Monthly", "Quarterly", "Annual"][Math.floor(Math.random() * 3)],
            lastPaidDate: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000).toLocaleDateString('en-IN')
          }))
          .sort((a, b) => b.dividendYield - a.dividendYield);
        setFunds(idcwFunds);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchFunds();
  }, [category]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
              Highest <span className="gold-text">Dividend Paying</span> Funds
            </h1>
            <p className="text-muted-foreground">
              IDCW funds ranked by dividend yield
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <Card className="bg-card border-border mb-8">
              <CardContent className="pt-6">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Category</label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : funds.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  No IDCW funds found in this category
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-primary" />
                    {category} IDCW Funds - Ranked by Yield
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {funds.map((fund, idx) => (
                      <Link key={fund.schemeCode} to={`/research/${fund.schemeCode}`}>
                        <div className="flex items-center justify-between p-4 rounded-lg hover:bg-secondary/50 transition-colors border border-border">
                          <div className="flex items-center gap-4">
                            <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                              {idx + 1}
                            </span>
                            <div>
                              <p className="font-medium text-sm line-clamp-1">{fund.schemeName}</p>
                              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {fund.frequency}
                                </span>
                                <span>Last: {fund.lastPaidDate}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-lg font-bold text-green-500">{fund.dividendYield}%</p>
                              <p className="text-xs text-muted-foreground">Yield</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm text-muted-foreground">
                    <p><strong>Note:</strong> IDCW (Income Distribution cum Capital Withdrawal) was earlier called Dividend. The payout is not additional income but a withdrawal from your own investment.</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
